import { memo, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDatePicker,
  MiIcon,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  PERMISSIONS,
} from "@miplatform/constants";
import {
  addToDate,
  addWorkDays,
  formatDate,
  getDate,
  isSameDay,
  isSameOrAfter,
  setTime,
  subtractFromDate,
} from "@miplatform/utils";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import { OrderAlerts } from "../OrderAlerts/OrderAlerts";
import { OrderHolidayIndicator } from "../OrderHolidayIndicator/OrderHolidayIndicator";
import { useNewOrderActionsViewModel } from "./useNewOrderActionsViewModel";

/**
 * NewOrderActions Component
 *
 * A React functional component that manages and approves order dates, allowing
 * users to view, adjust, and approve a requested start date.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.id - The unique identifier for the order.
 * @param {string} props.requestedStartDate - The initially requested start date for the order.
 * @param {string} props.modifiedOn - The timestamp indicating when the order was last modified.
 * @param {Function} props.onRefresh - Callback function invoked after successful approval to refresh the data.
 *
 * @returns {JSX.Element} The rendered NewOrderActions component.
 */
export const NewOrderActions = memo((props) => {
  const {
    id,
    requestedStartDate,
    modifiedOn,
    onRefresh,
    orderTemplateApprovalLeadTime,
    orderTemplateApprovalLeadDays,
    rescheduleReason,
    tenantHolidays,
  } = props;
  const { classes, theme } = useStyles();
  const { hasPermission } = usePermissions();

  const canApproveOrder = hasPermission(PERMISSIONS.ORDER.APPROVE);

  const { isLoading, approveOrder } = useNewOrderActionsViewModel({
    onRefresh,
  });
  const [approveDate, setApproveDate] = useState(getDate(requestedStartDate));
  const isBeyondLeadTime = (value) => {
    if (!orderTemplateApprovalLeadTime || !orderTemplateApprovalLeadDays)
      return false;

    const cutOffDate = addWorkDays(orderTemplateApprovalLeadDays);
    const approvedStartDate = setTime(value, orderTemplateApprovalLeadTime);

    if (isSameOrAfter(cutOffDate, approvedStartDate, "minute")) {
      return true;
    }

    return false;
  };

  const { alerts, isDateError } = useMemo(() => {
    const newAlerts = [];
    const isDateError = isBeyondLeadTime(approveDate);

    if (!isSameDay(approveDate, requestedStartDate)) {
      newAlerts.push("Approval Date does not match Requested Date");
    }
    if (isDateError) {
      newAlerts.push("Approval Date does not meet lead time for this order");
    }

    return { alerts: newAlerts, isDateError };
  }, [requestedStartDate, approveDate]);

  const holidayMatch = useMemo(() => {
    if (!approveDate || !tenantHolidays?.length) return null;
    const orderDate = moment(approveDate);
    return (
      tenantHolidays.find((h) => {
        const start = moment(h.startDate).startOf("day");
        const end = moment(h.endDate).endOf("day");
        return orderDate.isBetween(start, end, undefined, "[]");
      }) || null
    );
  }, [approveDate, tenantHolidays]);

  const handleResetApproveDate = () =>
    setApproveDate(getDate(requestedStartDate));

  const handleDecreaseApproveDate = () =>
    setApproveDate(subtractFromDate(approveDate, 1, "days"));

  const handleIncreaseApproveDate = () =>
    setApproveDate(addToDate(approveDate, 1, "days"));

  const handleDateChange = (value) => {
    setApproveDate(value);
  };

  const handleApprove = async () => {
    await approveOrder(id, {
      approvedStartDate: formatDate(
        approveDate,
        DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
      ),
      modifiedOn,
    });
  };

  const DATE_PICKER_WIDTH = 190;

  return (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
      {!rescheduleReason ? null : (
        <MiStackedTypography
          title="Reschedule Reason"
          description={rescheduleReason.label}
        />
      )}
      <MiStackedTypography
        title="Requested Date"
        description={formatDate(
          requestedStartDate,
          DATE_TIME_FORMATS.FORMAT_MM_DD_YY
        )}
      />
      <Box className={classes.datesContainer}>
        {alerts.length > 0 ? (
          <OrderAlerts
            variant={isDateError ? BUTTON_COLORS.RED : BUTTON_COLORS.ORANGE}
            alerts={alerts}
          />
        ) : null}
        <OrderHolidayIndicator holiday={holidayMatch} />
        <Box width={DATE_PICKER_WIDTH}>
          <MiDatePicker
            label="Approve Date"
            variant={DATEPICKER_VARIANTS.INLINE}
            actions={[DATEPICKER_ACTIONS.RESET, DATEPICKER_ACTIONS.CANCEL]}
            value={approveDate}
            onReset={handleResetApproveDate}
            onChange={handleDateChange}
            startAdornment={
              <MiIcon
                path={mdiChevronLeft}
                size={1.2}
                color={theme.palette.grey.main}
                style={{
                  cursor: "pointer",
                  marginTop: "auto",
                  marginBottom: "auto",
                }}
                role="button"
                onClick={handleDecreaseApproveDate}
              />
            }
            endAdornment={
              <MiIcon
                path={mdiChevronRight}
                size={1.2}
                color={theme.palette.grey.main}
                style={{
                  cursor: "pointer",
                  marginTop: "auto",
                  marginBottom: "auto",
                }}
                role="button"
                onClick={handleIncreaseApproveDate}
              />
            }
          />
        </Box>
        {canApproveOrder && (
          <MiButton
            title="Approve"
            variant={BUTTON_VARIANTS.CONTAINED}
            disabled={isLoading}
            onClick={handleApprove}
            sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2) }}
          />
        )}
      </Box>
      <OrdersListActionMenu
        menuViewConfig={{
          Reschedule: true,
          Hold: true,
          Cancel: true,
          OrderActivityDetails: true,
          ForceComplete: true,
          Push24h: false,
        }}
        {...props}
      />
    </Box>
  );
});

const useStyles = makeStyles()((theme) => ({
  datesContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xl")]: {
      justifyContent: "space-between",
    },
  },
}));
